import { useState, useRef, useCallback } from 'react';
import { API_BASE_URL } from '../../../config/api';

// Minimum loading time in milliseconds (exactly 5 seconds)
const MIN_LOADING_TIME = 5000;

export function useMoodRecommendations() {
  const [isLoading, setIsLoading] = useState(false);
  const requestInProgressRef = useRef(false);

  const getRecommendations = useCallback(async (type, input, count = 4, existingBooks = [], selectedBooks = []) => {
    // Prevent multiple simultaneous requests for this component instance
    if (requestInProgressRef.current) {
      console.log('Request already in progress for this component, skipping...');
      return null;
    }

    try {
      setIsLoading(true);
      requestInProgressRef.current = true;
      
      console.log('🔍 Making recommendation request:', {
        type,
        input,
        count,
        existingBooksCount: existingBooks.length,
        selectedBooksCount: selectedBooks.length
      });
      
      // Start timing the request
      const startTime = Date.now();
      
      const body = type === 'mood'
        ? {
            type: 'mood',
            mood: input,
            input,
            count,
            existingBooks
          }
        : {
            type: 'similar',
            input,
            count,
            existingBooks,
            selectedBooks
          };

      const response = await fetch(`${API_BASE_URL}/api/recommendations`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('Server error:', errorData);
        
        // Check if it's a limit reached error
        if (errorData.limitReached) {
          throw new Error('limit reached');
        }
        
        throw new Error(errorData.message || errorData.error || 'Failed to fetch recommendations');
      }

      const data = await response.json();
      
      // Handle both response formats:
      // 1. Direct array (similar books) - data is the array directly
      // 2. Wrapped object (mood recommendations) - data.recommendations is the array
      let recommendations;
      if (Array.isArray(data)) {
        // Similar books API returns array directly
        recommendations = data;
      } else if (data && data.recommendations && Array.isArray(data.recommendations)) {
        // Mood API returns wrapped object
        recommendations = data.recommendations;
      } else {
        throw new Error('Invalid response format from server');
      }

      console.log('🔍 Received recommendations:', {
        count: recommendations.length,
        recommendations: recommendations.map(r => r.title)
      });

      // Calculate how long to wait to meet minimum loading time
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);
      
      // Wait for the remaining time if needed
      if (remainingTime > 0) {
        await new Promise(resolve => setTimeout(resolve, remainingTime));
      }
      
      return recommendations;
    } catch (error) {
      console.error('Error in getRecommendations:', error);
      throw error;
    } finally {
      setIsLoading(false);
      requestInProgressRef.current = false;
    }
  }, []);

  // New function for 'see more recommendations' for Similar Books
  const getMoreSimilarBooks = useCallback(async (input, shownBooks) => {
    try {
      setIsLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/recommendations/see-more-similar`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ input, shownBooks })
      });
      if (!response.ok) {
        const errorData = await response.json();
        if (errorData.limitReached) {
          throw new Error('limit reached');
        }
        throw new Error(errorData.message || errorData.error || 'Failed to fetch more similar recommendations');
      }
      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error('Invalid response format from server');
      }
      return data;
    } catch (error) {
      console.error('Error in getMoreSimilarBooks:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { getRecommendations, getMoreSimilarBooks, isLoading };
} 